import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash/noop';

import { useLayoutContext } from '@ott/layout-context';
import { AutocompleteDropdown, findActiveOption, checkFullName } from './utils';

/*
  hover,↑,↓: onOptionChange(option, {updateActive: true})
  click: onOptionChange(option, {selectActive: true})
  enter,tab: onClose({selectActive: true}) -> onOptionChange(activeOption, {selectActive: true})
  esc: onClose({revertChanges: true}) -> onOptionChange(savedOption, {selectActive: true})
  blur: onClose({revertChanges: true, checkFullName: true}) ->
    if (isFullNameFilled) onOptionChange(activeOption, {selectActive: true})
    else if (isTriggerValueEmpty) onOptionChange(null, {selectActive: true})
    else onOptionChange(savedOption, {selectActive: true})
*/
const Autocomplete = React.memo(function AutocompleteNext({
  value,
  onChange,
  onOpen,
  onClose,
  onBlur,
  triggerProps,
  triggerProps: {
    value: triggerValue,
    onKeyDown: onTriggerKeyDown,
    onChange: onTriggerChange = _noop,
    onSearch = _noop,
  } = {},
  overlayProps,
  overlayProps: { options } = {},
  isLoading,
  revertChanges,
  fullScreenOnMobile,
  shouldRenderAirportCity,
  shouldCloseOverlayOnHeightChange,
}) {
  const layoutContext = useLayoutContext();
  const [isOpen, setOpenState] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  const [savedOption, setSavedOption] = useState(null);
  const isMobileLayout = fullScreenOnMobile && layoutContext.isMobileLayout;

  const onTextChange = useCallback(
    (text, isClear) => {
      onTriggerChange(text);
      onSearch(text, isClear); // вызываем onSearch только на ручное изменение input
    },
    [onTriggerChange, onSearch]
  );

  const onOptionChange = useCallback(
    (option, params = {}) => {
      setActiveOption(option);
      if (isMobileLayout && params) {
        delete params.selectNext;
      }
      if (params.selectActive) {
        onChange(option, params, triggerValue);
        if (option) {
          onTriggerChange(option.name || '');
        }
      }
    },
    [onChange, triggerValue, onTriggerChange]
  );

  const onOpenCallback = useCallback(() => {
    setOpenState(true);
    setSavedOption(value);
    onOpen();
  }, [value, onOpen]);

  const onCloseCallback = useCallback(
    (params = {}) => {
      setOpenState(false);
      if (params.revertChanges) {
        if (params.checkFullName && checkFullName(activeOption, triggerValue)) {
          onOptionChange(activeOption, { selectActive: true });
        } else if (revertChanges) {
          if (!triggerValue) {
            onOptionChange(null, { selectActive: true });
          } else {
            onOptionChange(savedOption, { ...params, selectActive: true });
          }
        } else {
          setActiveOption({});
        }
      }
      if (params.selectActive) {
        if (revertChanges || (params.selectByEnter && !isMobileLayout)) {
          onOptionChange(activeOption, params);
        } else {
          setActiveOption({});
        }
      }
      setSavedOption(null);
      onClose();
    },
    [savedOption, activeOption, onOptionChange, onClose, revertChanges, triggerValue]
  );

  // делаем активной первую опцию при измененнии значения в input
  useEffect(
    () => {
      if (!isOpen) {
        return;
      }
      const newActiveOption = findActiveOption(triggerValue, options);
      if (newActiveOption !== activeOption) {
        setActiveOption(newActiveOption);
      }
    },
    [triggerValue, options, isOpen] // activeOption не добавляем
  );

  // при изменении опции извне - обновляем значение в input
  useEffect(() => {
    setActiveOption(value);
    if (value) {
      onTriggerChange(value.name || '');
    }
  }, [value, onTriggerChange]);

  const actualTriggerProps = {
    ...triggerProps,
    shouldRenderAirportCity,
    isLoading,
    isMobileLayout,
    isMobileInput: false,
    option: !revertChanges ? {} : activeOption,
    onChange: onTextChange,
    onKeyDown: onTriggerKeyDown,
  };

  return (
    <AutocompleteDropdown
      onOpen={onOpenCallback}
      onClose={onCloseCallback}
      onBlur={onBlur}
      value={activeOption}
      closeOnOutsideClick={!isMobileLayout}
      closeOnResize={!isMobileLayout}
      shouldCloseOverlayOnHeightChange={shouldCloseOverlayOnHeightChange}
      triggerProps={actualTriggerProps}
      overlayProps={{
        ...overlayProps,
        shouldRenderAirportCity,
        isMobileLayout,
        onChange: onOptionChange,
        triggerProps: actualTriggerProps,
      }}
    />
  );
});

Autocomplete.defaultProps = {
  triggerProps: {},
  overlayProps: {},
  onChange: _noop,
  onOpen: _noop,
  onClose: _noop,
  revertChanges: false,
  fullScreenOnMobile: true,
  shouldCloseOverlayOnHeightChange: true,
};

Autocomplete.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  overlayProps: PropTypes.object.isRequired, // blackbox forwarded to Overlay component
  // eslint-disable-next-line react/forbid-prop-types
  triggerProps: PropTypes.object.isRequired, // blackbox forwarded to Trigger component
  value: PropTypes.object,
  onChange: PropTypes.func,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onBlur: PropTypes.func,
  isLoading: PropTypes.bool,
  revertChanges: PropTypes.bool,
  fullScreenOnMobile: PropTypes.bool,
  shouldCloseOverlayOnHeightChange: PropTypes.bool,
};

export default Autocomplete;
